/**
 * Phase 2 Slice 6 — Recents history state for ChatAssistant only.
 * Owns Active/Archived list, title search, cursor pagination, and lifecycle list mutations.
 * Does not own messages, streaming, or live conversation runtime.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/api/client';

export const RECENTS_PAGE_LIMIT = 20;
export const RECENTS_SEARCH_DEBOUNCE_MS = 300;
export const PERMANENT_DELETE_CONFIRM_PHRASE = 'DELETE_PERMANENTLY';
export const SAFE_RECENTS_LOAD_ERROR = "Couldn't load conversations";
export const SAFE_RECENTS_MUTATION_ERROR = 'Something went wrong. Please try again.';

/** @typedef {'active' | 'archived'} RecentsView */

/**
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeRecentsQuery(raw) {
  if (typeof raw !== 'string') return '';
  return raw.trim();
}

/**
 * @param {Array<{ id?: string }>} sessions
 * @returns {Array<{ id?: string }>}
 */
export function dedupeSessionsById(sessions) {
  const seen = new Set();
  const out = [];
  for (const session of sessions || []) {
    if (!session?.id || seen.has(session.id)) continue;
    seen.add(session.id);
    out.push(session);
  }
  return out;
}

/**
 * @param {string} phrase
 * @returns {boolean}
 */
export function isPermanentDeleteConfirmPhrase(phrase) {
  return phrase === PERMANENT_DELETE_CONFIRM_PHRASE;
}

/**
 * @param {*} err
 * @returns {boolean}
 */
export function isInvalidCursorListError(err) {
  const response = err && typeof err === 'object' ? err.response : undefined;
  const data = response && typeof response === 'object' ? response.data : undefined;
  const code = data && typeof data === 'object' ? data.code : undefined;
  if (
    code === 'INVALID_SESSION_CURSOR' ||
    code === 'SESSION_CURSOR_MISMATCH' ||
    code === 'INVALID_PAGINATION'
  ) {
    return true;
  }
  const status = response && typeof response === 'object' ? response.status : undefined;
  if (status !== 400) return false;
  const message = String(
    data && typeof data === 'object' && data.error != null ? data.error : '',
  );
  return /cursor|pagination/i.test(message);
}

/**
 * @param {*} err
 * @returns {string}
 */
export function safeRecentsErrorMessage(err) {
  const response = err && typeof err === 'object' ? err.response : undefined;
  const status = response && typeof response === 'object' ? response.status : undefined;
  if (status === 401) return 'Please sign in again';
  if (status === 403 || status === 404) return 'Unavailable';
  return SAFE_RECENTS_LOAD_ERROR;
}

/**
 * Append page rows without duplicates.
 * @param {any[]} existing
 * @param {any[]} incoming
 */
export function appendSessionPages(existing, incoming) {
  return dedupeSessionsById([...(existing || []), ...(incoming || [])]);
}

/**
 * @param {{
 *   profileId: string | null | undefined,
 * }} args
 */
export function useChatRecentsHistory({ profileId }) {
  const [view, setViewState] = useState(/** @type {RecentsView} */ ('active'));
  const [queryInput, setQueryInput] = useState('');
  const [effectiveQuery, setEffectiveQuery] = useState('');
  const [sessions, setSessions] = useState(/** @type {any[]} */ ([]));
  const [nextCursor, setNextCursor] = useState(/** @type {string | null} */ (null));
  const [hasMore, setHasMore] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(/** @type {string | null} */ (null));
  const [loadMoreError, setLoadMoreError] = useState(/** @type {string | null} */ (null));
  const [mutationSessionId, setMutationSessionId] = useState(/** @type {string | null} */ (null));
  const [announcement, setAnnouncement] = useState('');

  const requestGenRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const cursorRecoveryUsedRef = useRef(false);
  const profileIdRef = useRef(profileId);
  profileIdRef.current = profileId;

  // Debounce title search (~300ms). Whitespace-only → empty query.
  useEffect(() => {
    const handle = setTimeout(() => {
      setEffectiveQuery(normalizeRecentsQuery(queryInput));
    }, RECENTS_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [queryInput]);

  const fetchPage = useCallback(
    async ({
      profile,
      listView,
      q,
      cursor = null,
      append,
      generation,
      isRecovery = false,
    }) => {
      if (!profile) {
        setSessions([]);
        setNextCursor(null);
        setHasMore(false);
        setLoadError(null);
        setLoadMoreError(null);
        setLoadingInitial(false);
        setLoadingMore(false);
        return;
      }

      if (append) {
        setLoadingMore(true);
        setLoadMoreError(null);
      } else {
        setLoadingInitial(true);
        setLoadError(null);
        setLoadMoreError(null);
      }

      const opts = {
        profileId: profile,
        pagination: /** @type {const} */ ('cursor'),
        limit: RECENTS_PAGE_LIMIT,
        ...(cursor ? { cursor } : {}),
        ...(q ? { q } : {}),
      };

      try {
        const res =
          listView === 'archived'
            ? await api.chat.sessionsArchived(opts)
            : await api.chat.sessions(opts);

        if (generation !== requestGenRef.current || profileIdRef.current !== profile) {
          return;
        }

        const pageSessions = Array.isArray(res.data?.sessions) ? res.data.sessions : [];
        const pageHasMore = Boolean(res.data?.hasMore);
        const pageCursor =
          typeof res.data?.nextCursor === 'string' && res.data.nextCursor.length > 0
            ? res.data.nextCursor
            : null;

        setSessions((prev) => (append ? appendSessionPages(prev, pageSessions) : pageSessions));
        setHasMore(pageHasMore);
        setNextCursor(pageCursor);
        setLoadError(null);
        setLoadMoreError(null);
        if (!isRecovery) cursorRecoveryUsedRef.current = false;
      } catch (err) {
        if (generation !== requestGenRef.current || profileIdRef.current !== profile) {
          return;
        }

        if (!append && isInvalidCursorListError(err) && !cursorRecoveryUsedRef.current) {
          cursorRecoveryUsedRef.current = true;
          const retryGen = ++requestGenRef.current;
          await fetchPage({
            profile,
            listView,
            q,
            cursor: null,
            append: false,
            generation: retryGen,
            isRecovery: true,
          });
          return;
        }

        const message = safeRecentsErrorMessage(err);
        if (append) {
          setLoadMoreError(message);
        } else {
          setSessions([]);
          setNextCursor(null);
          setHasMore(false);
          setLoadError(message);
        }
      } finally {
        if (generation === requestGenRef.current) {
          if (append) {
            setLoadingMore(false);
            loadingMoreRef.current = false;
          } else {
            setLoadingInitial(false);
          }
        }
      }
    },
    [],
  );

  const reloadCurrent = useCallback(() => {
    if (!profileId) {
      setSessions([]);
      setNextCursor(null);
      setHasMore(false);
      setLoadError(null);
      return;
    }
    const generation = ++requestGenRef.current;
    cursorRecoveryUsedRef.current = false;
    void fetchPage({
      profile: profileId,
      listView: view,
      q: effectiveQuery,
      cursor: null,
      append: false,
      generation,
    });
  }, [profileId, view, effectiveQuery, fetchPage]);

  // Initial / reset load when profile, view, or effective query changes.
  useEffect(() => {
    const generation = ++requestGenRef.current;
    cursorRecoveryUsedRef.current = false;
    loadingMoreRef.current = false;
    setNextCursor(null);
    setHasMore(false);
    void fetchPage({
      profile: profileId,
      listView: view,
      q: effectiveQuery,
      cursor: null,
      append: false,
      generation,
    });
  }, [profileId, view, effectiveQuery, fetchPage]);

  const setView = useCallback(
    (nextView) => {
      const normalised = nextView === 'archived' ? 'archived' : 'active';
      if (normalised === view) return;
      setQueryInput('');
      setEffectiveQuery('');
      setViewState(normalised);
    },
    [view],
  );

  const setQuery = useCallback((value) => {
    setQueryInput(typeof value === 'string' ? value : '');
  }, []);

  const clearQuery = useCallback(() => {
    setQueryInput('');
    setEffectiveQuery('');
  }, []);

  const loadMore = useCallback(() => {
    if (!profileId || !hasMore || !nextCursor || loadingInitial || loadingMoreRef.current) {
      return;
    }
    loadingMoreRef.current = true;
    const generation = requestGenRef.current;
    void fetchPage({
      profile: profileId,
      listView: view,
      q: effectiveQuery,
      cursor: nextCursor,
      append: true,
      generation,
    });
  }, [profileId, hasMore, nextCursor, loadingInitial, view, effectiveQuery, fetchPage]);

  const updateSessionTitle = useCallback((sessionId, title) => {
    setSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, title } : s)),
    );
  }, []);

  const removeSession = useCallback((sessionId) => {
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  const archiveSession = useCallback(
    async (sessionId) => {
      if (!sessionId || mutationSessionId) return { ok: false, reason: 'busy' };
      const profileAtStart = profileIdRef.current;
      const genAtStart = requestGenRef.current;
      setMutationSessionId(sessionId);
      try {
        await api.chat.archiveSession(sessionId);
        // WP01 U42: late A mutation must not rewrite B's recents UI after Profile switch.
        if (
          profileAtStart !== profileIdRef.current
          || genAtStart !== requestGenRef.current
        ) {
          return { ok: false, reason: 'stale_profile' };
        }
        removeSession(sessionId);
        setAnnouncement('Chat archived');
        return { ok: true };
      } catch {
        if (profileAtStart === profileIdRef.current) setAnnouncement('');
        return { ok: false, reason: 'error', message: SAFE_RECENTS_MUTATION_ERROR };
      } finally {
        setMutationSessionId(null);
      }
    },
    [mutationSessionId, removeSession],
  );

  const restoreSession = useCallback(
    async (sessionId) => {
      if (!sessionId || mutationSessionId) return { ok: false, reason: 'busy' };
      const profileAtStart = profileIdRef.current;
      const genAtStart = requestGenRef.current;
      setMutationSessionId(sessionId);
      try {
        await api.chat.restoreSession(sessionId);
        if (
          profileAtStart !== profileIdRef.current
          || genAtStart !== requestGenRef.current
        ) {
          return { ok: false, reason: 'stale_profile' };
        }
        removeSession(sessionId);
        setAnnouncement('Chat restored');
        return { ok: true };
      } catch {
        if (profileAtStart === profileIdRef.current) setAnnouncement('');
        return { ok: false, reason: 'error', message: SAFE_RECENTS_MUTATION_ERROR };
      } finally {
        setMutationSessionId(null);
      }
    },
    [mutationSessionId, removeSession],
  );

  const permanentlyDeleteSession = useCallback(
    async (sessionId, confirm) => {
      if (!sessionId || mutationSessionId) return { ok: false, reason: 'busy' };
      if (!isPermanentDeleteConfirmPhrase(confirm)) {
        return { ok: false, reason: 'confirm' };
      }
      const profileAtStart = profileIdRef.current;
      const genAtStart = requestGenRef.current;
      setMutationSessionId(sessionId);
      try {
        const res = await api.chat.deleteSession(sessionId, confirm);
        if (
          profileAtStart !== profileIdRef.current
          || genAtStart !== requestGenRef.current
        ) {
          return { ok: false, reason: 'stale_profile' };
        }
        removeSession(sessionId);
        setAnnouncement('Chat deleted');
        return {
          ok: true,
          alreadyDeleted: Boolean(res.data?.alreadyDeleted),
        };
      } catch {
        if (profileAtStart === profileIdRef.current) setAnnouncement('');
        return { ok: false, reason: 'error', message: SAFE_RECENTS_MUTATION_ERROR };
      } finally {
        setMutationSessionId(null);
      }
    },
    [mutationSessionId, removeSession],
  );

  return {
    view,
    setView,
    query: queryInput,
    setQuery,
    clearQuery,
    effectiveQuery,
    sessions,
    nextCursor,
    hasMore,
    loadingInitial,
    loadingMore,
    loadError,
    loadMoreError,
    mutationSessionId,
    announcement,
    setAnnouncement,
    loadMore,
    reloadCurrent,
    updateSessionTitle,
    removeSession,
    archiveSession,
    restoreSession,
    permanentlyDeleteSession,
  };
}
